/*
 * The decision log — every signal a rule produced, acted on or not.
 *
 * This page exists to answer one question, and it is the question this
 * application gets asked more than any other: "the market moved, why did
 * nothing happen?"
 *
 * The answer is almost never a bug. It is that the day's trade limit was
 * already reached, or the position was already held, or the entry window had
 * closed, or the loss limit had tripped. The engine records that reason
 * against the signal rather than discarding it, and this is where it is read.
 * So the default filter is "not taken" — the ones that did fire are visible in
 * Orders.
 */

import { useState } from "react";

import { useSignals, useStatus, useStrategies } from "../api/hooks";
import { IconCheck } from "../components/Icons";
import { clockFromMs, price as fmtPrice } from "../lib/format";

type Filter = "all" | "acted" | "skipped";

export function SignalsPage() {
  const status = useStatus();
  const signals = useSignals(300, status.data?.marketOpen);
  const strategies = useStrategies();
  const [filter, setFilter] = useState<Filter>("all");

  const byId = new Map((strategies.data ?? []).map((s) => [s.id, s.name]));
  const all = signals.data ?? [];
  const rows = all.filter((s) =>
    filter === "acted" ? s.acted : filter === "skipped" ? !s.acted : true,
  );
  const skipped = all.filter((s) => !s.acted).length;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Decisions</h1>
          <p>
            Every signal each rule produced, and — where it did not become an order —
            the reason it did not. This is where “why did nothing happen?” is answered.
          </p>
        </div>
        <div className="page-actions">
          <div className="chip-group">
            <button className={filter === "all" ? "on" : ""} onClick={() => setFilter("all")}>
              All {all.length}
            </button>
            <button className={filter === "acted" ? "on" : ""} onClick={() => setFilter("acted")}>
              Acted on
            </button>
            <button className={filter === "skipped" ? "on" : ""} onClick={() => setFilter("skipped")}>
              Not taken {skipped > 0 ? skipped : ""}
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        {rows.length === 0 ? (
          <div className="empty">
            <strong>Nothing yet</strong>
            A signal is recorded every time a rule decides something — including when
            the decision was refused.
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Seen</th>
                  <th>Symbol</th>
                  <th>Signal</th>
                  <th className="num">At</th>
                  <th>Because</th>
                  <th>Strategy</th>
                  <th>Outcome</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.id}>
                    <td className="small faint nowrap">{clockFromMs(s.atMs)}</td>
                    <td>
                      <div className="sym">{s.tradingsymbol}</div>
                      <div className="sub">{s.exchange}</div>
                    </td>
                    <td>
                      <span className={`pill ${s.side === "BUY" ? "buy" : "sell"}`}>{s.side}</span>
                      <div className="sub">{s.intent}</div>
                    </td>
                    <td className="num">{fmtPrice(s.price)}</td>
                    <td className="small" style={{ whiteSpace: "normal", maxWidth: 300 }}>
                      {s.reason}
                    </td>
                    <td className="small faint">{byId.get(s.strategyId) ?? "—"}</td>
                    <td>
                      {s.acted ? (
                        <span className="up small">
                          <IconCheck size={12} /> Order placed
                        </span>
                      ) : (
                        <span
                          className="warn small"
                          style={{ whiteSpace: "normal", display: "block", maxWidth: 280 }}
                        >
                          {s.skippedReason ?? "Not taken"}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="tiny faint mt">
        The same candle is examined many times a minute, so a signal is recorded once
        per bar per instrument — that is what stops one crossover becoming ten orders.
      </p>
    </>
  );
}
